import { useAuthState } from '@auth';
import { authRoutes } from '@auth/routes';
import { UserRoles } from '@core/domain';
import { LoadingOverlay } from '@soteria/design-system-ve';
import type { ReactNode } from 'react';
import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { useCommunityDetailForAudit } from '../hooks';

interface CommunityAuditGuardProps {
  children: ReactNode;
}

/**
 * Community Audit Guard Component
 *
 * Protects audit routes by checking if the user has permission to access audit functionality.
 * Grants access to users with:
 * - System Admin role (from user.roles)
 * - QA role in the specific community (from community.roles)
 *
 * NOTE: Community Admin roles do NOT have audit access (backend GraphQL resolvers reject them).
 * Only QA roles can access audit data and functionality.
 *
 * Uses audit-specific query that excludes moderationStats to prevent failures when
 * Moderation Service is unavailable or returns invalid data.
 */
export function CommunityAuditGuard({ children }: CommunityAuditGuardProps) {
  const { hasRoleAccess } = useAuthState();
  const { community, loading, errors } = useCommunityDetailForAudit();
  const navigate = useNavigate();

  // Enhanced authorization check with hierarchical role support
  // NOTE: Only QA roles have access to audit data (Admin roles are rejected by backend)
  const hasAccess = useMemo(() => {
    if (!community) return false;

    // Check hierarchical QA access - ONLY QA roles can access audit data
    const hasQAPermission = hasRoleAccess(UserRoles.QA, community.communityGroupId, community.id);

    // Admin roles do NOT have audit access (backend GraphQL resolvers reject them)
    // Removed hasAdminAccess check to match backend authorization
    return hasQAPermission;
  }, [community, hasRoleAccess]);

  // Determine if the user should be denied access
  const shouldDeny = !loading && ((Array.isArray(errors) && errors.length > 0) || !community || !hasAccess);

  // Navigate to not-found page when access is denied
  // Uses useEffect to ensure React Router navigation is processed as a side effect,
  // not during render — calling navigate() during render is unreliable in React 18+
  useEffect(() => {
    if (shouldDeny) {
      navigate(authRoutes.notFound);
    }
  }, [shouldDeny, navigate]);

  // Show loading state while fetching community data
  if (loading) {
    return <LoadingOverlay fullScreen message="Loading..." />;
  }

  // Show loading overlay while redirect is in progress
  if (shouldDeny) {
    return null;
  }

  // User has permission - render the protected content
  return children;
}
